// useHervorheben.js — ruhige Animationen für Hallenschirme und Listen (To-do 496, 19.10.2026)
//
// Drei Bausteine, keine Fremdbibliothek:
//   useHervorheben(eintraege, schluessel, signatur)  → Set der Schlüssel, die
//       neu sind oder sich geändert haben (für 1,8 s); Klasse `anim-leuchten`.
//   useFlip(behaelterRef, abhaengigkeit)             → Positionswechsel per
//       FLIP: Kinder mit data-flip="<id>" gleiten an ihren neuen Platz.
//   useAnimierteListe(eintraege, schluessel, signatur) → alles zusammen:
//       entfernte Einträge bleiben 300 ms als `anim-weg` stehen, neue bekommen
//       `anim-ein`, geänderte `anim-leuchten`, Plätze tauschen per FLIP.
//
// Verglichen wird NUR über stabile IDs und eine Signatur (Text). Liefert das
// Polling dieselben Daten noch einmal, ändert sich der Fingerabdruck nicht,
// der Effekt läuft nicht, nichts flackert.
//
// Animiert werden nur transform und opacity (styles/animationen.css). Bei
// prefers-reduced-motion: reduce gibt es weder Hervorhebung noch FLIP noch
// Ausblenden, entfernte Einträge verschwinden sofort.
//
// `null` als eintraege heißt „noch nicht geladen“: dann wird kein Ausgangsstand
// gemerkt, und der erste echte Stand leuchtet nicht komplett auf.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../styles/animationen.css';

export const ANIM_DAUER = { ein: 360, aus: 300, flip: 450, leuchten: 1800 };

const LEER = new Set();
const TRENNER_FELD = '\u0001';
const TRENNER_EINTRAG = '\u0002';

export function bewegungReduziert() {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const ohneSignatur = () => '';

function paareAus(eintraege, schluessel, signatur) {
  if (!Array.isArray(eintraege)) return null;
  return eintraege.map((e, i) => [String(schluessel(e, i)), String(signatur(e, i) ?? '')]);
}

function abdruckAus(paare) {
  if (!paare) return null;
  return paare.map(([k, s]) => k + TRENNER_FELD + s).join(TRENNER_EINTRAG);
}

/**
 * Welche Einträge sind seit dem letzten Stand neu oder anders?
 * @returns {Set<string>} Schlüssel (als Text), die gerade leuchten sollen
 */
export function useHervorheben(eintraege, schluessel, signatur = ohneSignatur,
  { dauer = ANIM_DAUER.leuchten, neueMarkieren = true } = {}) {
  const paare = paareAus(eintraege, schluessel, signatur);
  const abdruck = abdruckAus(paare);
  const paareRef = useRef(paare);
  paareRef.current = paare;
  const vorher = useRef(null);
  const zeitgeber = useRef([]);
  const [markiert, setMarkiert] = useState(LEER);

  useEffect(() => {
    const aktuell = paareRef.current;
    if (!aktuell) return;
    const jetzt = new Map(aktuell);
    const alt = vorher.current;
    vorher.current = jetzt;
    if (!alt) return;
    const geaendert = [];
    jetzt.forEach((sig, k) => {
      if (!alt.has(k)) { if (neueMarkieren) geaendert.push(k); }
      else if (alt.get(k) !== sig) geaendert.push(k);
    });
    if (!geaendert.length || bewegungReduziert()) return;
    setMarkiert((m) => new Set([...m, ...geaendert]));
    zeitgeber.current.push(setTimeout(() => {
      setMarkiert((m) => {
        const n = new Set(m);
        geaendert.forEach((k) => n.delete(k));
        return n.size ? n : LEER;
      });
    }, dauer));
  }, [abdruck, dauer, neueMarkieren]);

  useEffect(() => {
    const zg = zeitgeber.current;
    return () => zg.forEach(clearTimeout);
  }, []);

  return markiert;
}

/**
 * FLIP: Kinder des Behälters mit data-flip="<id>" gleiten von ihrer alten an
 * die neue Stelle (nur transform). `abhaengigkeit` ist ein Text, der sich
 * genau dann ändert, wenn sich die Reihenfolge ändern kann.
 */
export function useFlip(behaelterRef, abhaengigkeit, { dauer = ANIM_DAUER.flip } = {}) {
  const lagen = useRef(null);

  useLayoutEffect(() => {
    const el = behaelterRef.current;
    if (!el) { lagen.current = null; return; }
    const kinder = Array.from(el.querySelectorAll('[data-flip]'));
    // Laufende Verschiebungen zurücksetzen, damit die echte Lage gemessen wird
    kinder.forEach((k) => { k.style.transition = ''; k.style.transform = ''; });
    const basis = el.getBoundingClientRect();
    const neu = new Map();
    kinder.forEach((k) => {
      const r = k.getBoundingClientRect();
      // Lage ohne laufende Animation messen: ein Einblenden (translateX/Y)
      // stünde sonst als falscher Ausgangspunkt in der nächsten Messung.
      let ex = 0;
      let ey = 0;
      try {
        const tf = getComputedStyle(k).transform;
        if (tf && tf !== 'none' && typeof DOMMatrixReadOnly === 'function') {
          const m = new DOMMatrixReadOnly(tf);
          ex = m.e; ey = m.f;
        }
      } catch { /* ohne Abzug messen */ }
      neu.set(k.dataset.flip, { x: r.left - basis.left - ex, y: r.top - basis.top - ey });
    });
    const alt = lagen.current;
    lagen.current = neu;
    if (!alt || bewegungReduziert()) return;

    // Ein Element, das React beim Umsortieren im DOM verschiebt, wird neu
    // eingehängt und spielt dabei seine Einblend-Animation von vorn (gemessen
    // 19.10.2026: fdpHerein lief beim Platztausch erneut). Die Animation
    // überschriebe die FLIP-Verschiebung und ließe die Zeile aufblinken.
    // Für schon vorhandene Einträge wird sie daher sofort beendet; ein
    // ausblendender Eintrag (anim-weg) behält seine.
    kinder.forEach((k) => {
      if (!alt.has(k.dataset.flip) || k.classList.contains('anim-weg') || !k.getAnimations) return;
      k.getAnimations().forEach((a) => {
        try {
          const timing = a.effect && a.effect.getTiming ? a.effect.getTiming() : null;
          if (a.animationName && timing && Number.isFinite(timing.iterations) && (a.currentTime || 0) < 100) a.finish();
        } catch { /* nicht beendbar: stehen lassen */ }
      });
    });

    const bewegt = [];
    kinder.forEach((k) => {
      const a = alt.get(k.dataset.flip);
      const n = neu.get(k.dataset.flip);
      if (!a || !n) return;
      const dx = a.x - n.x;
      const dy = a.y - n.y;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      k.style.transition = 'none';
      k.style.transform = `translate(${dx}px, ${dy}px)`;
      bewegt.push(k);
    });
    if (!bewegt.length) return;
    // Erzwingt das Zeichnen an der alten Stelle, bevor es losgleitet
    void el.offsetHeight;
    const rahmen = requestAnimationFrame(() => {
      bewegt.forEach((k) => {
        k.style.transition = `transform ${dauer}ms cubic-bezier(0.2, 0.7, 0.2, 1)`;
        k.style.transform = '';
      });
    });
    const aufraeumen = setTimeout(() => {
      bewegt.forEach((k) => { k.style.transition = ''; });
    }, dauer + 50);
    return () => { cancelAnimationFrame(rahmen); clearTimeout(aufraeumen); };
  }, [behaelterRef, abhaengigkeit, dauer]);
}

/**
 * Liste mit Einblenden, Ausblenden, Hervorheben und FLIP.
 * @returns {{ zeilen: Array<{schluessel:string, eintrag:any, weg:boolean, markiert:boolean}>,
 *             behaelterRef: object, klasse: (zeile) => string }}
 */
export function useAnimierteListe(eintraege, schluessel, signatur = ohneSignatur,
  { dauerAus = ANIM_DAUER.aus, leuchten = true, neueMarkieren = false } = {}) {
  const liste = Array.isArray(eintraege) ? eintraege : null;
  const markiert = useHervorheben(liste, schluessel, signatur, { neueMarkieren });

  const schluesselText = liste ? liste.map((e, i) => String(schluessel(e, i))).join(TRENNER_EINTRAG) : null;
  // Entfernte Einträge werden schon BEIM RENDERN erkannt, nicht erst in einem
  // Effekt: sonst gäbe es einen Zwischenstand ohne den Eintrag (die übrigen
  // springen an seinen Platz und wieder zurück, gemessen 19.10.2026).
  const vorher = useRef(null);            // Map schluessel -> { eintrag, index }
  const letzterText = useRef(null);
  const weg = useRef(new Map());          // schluessel -> { eintrag, index }
  const [, setTakt] = useState(0);
  if (liste && schluesselText !== letzterText.current) {
    const jetzt = new Map(liste.map((e, i) => [String(schluessel(e, i)), { eintrag: e, index: i }]));
    if (vorher.current && !bewegungReduziert()) {
      vorher.current.forEach((v, k) => { if (!jetzt.has(k) && !weg.current.has(k)) weg.current.set(k, v); });
    }
    // Wiedergekehrte Einträge nicht doppelt zeigen
    weg.current.forEach((v, k) => { if (jetzt.has(k)) weg.current.delete(k); });
    vorher.current = jetzt;
    letzterText.current = schluesselText;
  }

  useEffect(() => {
    if (!weg.current.size) return undefined;
    const t = setTimeout(() => { weg.current.clear(); setTakt((x) => x + 1); }, dauerAus);
    return () => clearTimeout(t);
  }, [schluesselText, dauerAus]);

  const zeilen = (liste || []).map((e, i) => {
    const k = String(schluessel(e, i));
    return { schluessel: k, eintrag: e, weg: false, markiert: leuchten && markiert.has(k) };
  });
  // Ausblendende an ihrer alten Stelle einsetzen, aufsteigend nach altem
  // Index: jede Einfügung schiebt die späteren so, wie sie vorher standen
  [...weg.current].sort((a, b) => a[1].index - b[1].index).forEach(([k, w]) => {
    if (zeilen.some((z) => z.schluessel === k)) return;
    zeilen.splice(Math.min(w.index, zeilen.length), 0,
      { schluessel: k, eintrag: w.eintrag, weg: true, markiert: false });
  });

  const behaelterRef = useRef(null);
  useFlip(behaelterRef, zeilen.map((z) => z.schluessel).join(TRENNER_EINTRAG));

  const klasse = (z) => (z.weg ? 'anim-weg' : 'anim-ein') + (z.markiert ? ' anim-leuchten' : '');
  return { zeilen, behaelterRef, klasse };
}
